import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import axios from "axios";
import { Box, Button, Typography } from "@mui/material";
import { ReactComponent as MicrophoneIcon } from "../assets/microphone.svg";

const VoiceRecorder = forwardRef(
  ({ doneRecording, assessmentComplete }, ref) => {
    const [recording, setRecording] = useState(false);
    const [audioUrl, setAudioUrl] = useState(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const gainNodeRef = useRef(null);
    const silenceTimerRef = useRef(null);
    const silenceIntervalRef = useRef(null); // new ref for interval
    const [fileName, setFileName] = useState("Test");
    const [fileToUpload, setFileToUpload] = useState(null);

    const streamRef = useRef(null);
    const sourceRef = useRef(null);

    useImperativeHandle(ref, () => ({
      startRecording,
      uploadFileToS3,
    }));

    const startRecording = async (assessmentId) => {
      setFileName(`${assessmentId}.webm`);

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream; // keep a handle so we can stop tracks later

      audioContextRef.current = new (window.AudioContext ||
        window.webkitAudioContext)();
      sourceRef.current =
        audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      sourceRef.current.connect(analyserRef.current);
      gainNodeRef.current = audioContextRef.current.createGain();
      gainNodeRef.current.gain.value = 0;
      analyserRef.current.connect(gainNodeRef.current);
      gainNodeRef.current.connect(audioContextRef.current.destination);

      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = []; // reset
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.start();
      setRecording(true);
      monitorSilence();
    };


    // Optionally, you can also start monitoring in a useEffect:
    // useEffect(() => {
    //   if (recording) {
    //     monitorSilence();
    //   }
    // }, [recording]);

    const monitorSilence = () => {
      const analyser = analyserRef.current;
      if (!analyser) return;
      const dataArray = new Uint8Array(analyser.fftSize);

      const checkSilence = () => {
        analyser.getByteTimeDomainData(dataArray);
        const sum = dataArray.reduce(
          (acc, value) => acc + (value - 128) ** 2,
          0
        );
        const average = Math.sqrt(sum / dataArray.length) / 128;

        // If volume is below threshold, start the silence timer
        if (average < 0.01) {
          if (silenceTimerRef.current === null) {
            silenceTimerRef.current = setTimeout(() => {
              stopRecording();
            }, 4000);
          }
        } else {
          // If noise is detected, clear the timer
          if (silenceTimerRef.current !== null) {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
          }
        }
      };

      // Check silence every 100ms
      silenceIntervalRef.current = setInterval(checkSilence, 100);
    };

    const stopRecording = () => {
      // stop silence checks immediately
      if (silenceIntervalRef.current) {
        clearInterval(silenceIntervalRef.current);
        silenceIntervalRef.current = null;
      }
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }

      if (!mediaRecorderRef.current) return;

      mediaRecorderRef.current.onstop = async () => {
        // build the blob
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        audioChunksRef.current = []; // release chunk refs ASAP

        // create URL for preview (if you need it)
        const url = URL.createObjectURL(blob);
        setAudioUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev); // revoke any previous URL
          return url;
        });

        // file for upload
        const file = new File([blob], fileName, { type: "audio/webm" });
        setFileToUpload(file);

        // release blob reference (the File still holds data)
        // eslint-disable-next-line no-unused-vars
        // blob = null; // (optional in JS; GC will collect once unreferenced)

        await cleanupMedia();
        setRecording(false);
        doneRecording();
      };

      // ensure final dataavailable fires on some browsers
      try {
        mediaRecorderRef.current.requestData?.();
      } catch {}
      mediaRecorderRef.current.stop();
    };


    const uploadFileToS3 = async (presignedUrl) => {
      try {
        const response = await axios.put(presignedUrl, fileToUpload, {
          headers: { "Content-Type": "audio/webm" },
        });

        if (response.status === 200) {
          // release memory now that server has it
          if (audioUrl) {
            URL.revokeObjectURL(audioUrl);
            setAudioUrl(null);
          }
          setFileToUpload(null);

          assessmentComplete();
        } else {
          console.error("File upload failed:", response.data);
        }
      } catch (error) {
        console.error(
          "Error uploading file:",
          error.response ? error.response.data : error.message
        );
      }
    };


    const cleanupMedia = async () => {
      // stop mic tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }

      // disconnect audio nodes
      try {
        sourceRef.current?.disconnect();
      } catch {}
      try {
        analyserRef.current?.disconnect();
      } catch {}
      try {
        gainNodeRef.current?.disconnect();
      } catch {}
      sourceRef.current = null;
      analyserRef.current = null;
      gainNodeRef.current = null;

      // close audio context
      if (audioContextRef.current) {
        try {
          await audioContextRef.current.close();
        } catch {}
        audioContextRef.current = null;
      }

      // clear recorder + handlers
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.ondataavailable = null;
        mediaRecorderRef.current.onstop = null;
        mediaRecorderRef.current = null;
      }

      // clear timers
      if (silenceIntervalRef.current) {
        clearInterval(silenceIntervalRef.current);
        silenceIntervalRef.current = null;
      }
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
    };


    return (
      <Box>
        {recording && (
          <Box
            sx={{
              position: "fixed",
              bottom: 25,
              left: "50%",
              transform: "translateX(-50%)",
              width: 150,
              // backgroundColor: "#7CFDFF",
              backgroundColor: "#212121",
              boxShadow: 3,
              borderRadius: 8,
              p: 2,
              zIndex: 1000,
              color: "#ccc",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {/* Pink circle with microphone inside */}
              <Box
                sx={{
                  backgroundColor: "#FF437C",
                  borderRadius: "50%",
                  width: 50,
                  height: 50,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1,
                }}
              >
                <MicrophoneIcon width={24} height={24} />
              </Box>
              <Typography variant="h5" ml={1}>
                Recording...
              </Typography>
              {/* MUI stop recording button */}
              <Button
                variant="contained"
                color="#7CFDFF"
                onClick={stopRecording}
                disabled={!recording}
                sx={{ mt: 2 }}
              >
                I'm done
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    );
  }
);

export default VoiceRecorder;
